import { motion } from 'framer-motion';
import { useThemeStore } from '../store/themeStore';
import { useMemo } from 'react';
import './AnimatedRainbowBorder.css';

/**
 * Animated rainbow border that adapts to the current theme
 * Uses theme colors to create a harmonious gradient animation
 * Features a flowing wave effect with pulsing glow
 */
interface Props {
  /** Which edge of the parent to anchor to. Defaults to 'bottom' (original behaviour). */
  placement?: 'bottom' | 'top';
}

export const AnimatedRainbowBorder = ({ placement = 'bottom' }: Props) => {
  const { currentTheme } = useThemeStore();

  // Generate theme-aware rainbow colors
  const gradientColors = useMemo(() => {
    const colors = currentTheme.colors;
    // Use vibrant theme colors for the rainbow effect
    return [
      colors.accent,
      colors.info,
      colors.success,
      colors.warning,
      colors.error,
      colors.ema9,
      colors.ema21,
      colors.accent, // Loop back for seamless animation
    ];
  }, [currentTheme]);

  // Create the gradient string
  const gradient = useMemo(() => {
    const colorStops = gradientColors.map((color, i) => {
      const percent = (i / (gradientColors.length - 1)) * 100;
      return `${color} ${percent}%`;
    });
    return `linear-gradient(90deg, ${colorStops.join(', ')})`;
  }, [gradientColors]);

  return (
    <div className={`rainbow-border-container${placement === 'top' ? ' placement-top' : ''}`}>
      {/* Main border line with slow elegant animation */}
      <motion.div
        className="rainbow-border"
        style={{
          background: gradient,
          backgroundSize: '200% 100%',
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 12,
          ease: 'linear',
          repeat: Infinity,
        }}
      />
      {/* Primary glow effect - synced with border */}
      <motion.div
        className="rainbow-border-glow"
        style={{
          background: gradient,
          backgroundSize: '200% 100%',
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 12,
          ease: 'linear',
          repeat: Infinity,
        }}
      />
      {/* Secondary wider glow for depth */}
      <motion.div
        className="rainbow-border-glow-wide"
        style={{
          background: gradient,
          backgroundSize: '200% 100%',
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 12,
          ease: 'linear',
          repeat: Infinity,
        }}
      />
    </div>
  );
};

export default AnimatedRainbowBorder;
